import { useEffect, useRef, useState } from "react";

const testimonials = [
  {
    quote: (
      <>
        The course did a great job explaining AI - from development through
        application. I appreciated the varying perspectives presented, which
        were helpful in understanding how to use AI responsibly as a tool in my
        profession, rather than a novelty.
      </>
    ),
    name: "Douglas M.",
    initials: "CM",
    image: "", // optional: "/avatars/cris.jpg"
    gradient: "from-[#8c2f39] to-[#3d1f2b]",
  },
  {
    quote: (
      <>
        Fillop was truly <b>a game-changer and a great guide</b> for me as we
        brought Dimensional to life.
      </>
    ),
    name: "Niholas T. H.",
    initials: "NH",
    image: "",
    gradient: "from-[#4a4f55] to-[#1f2326]",
  },
  {
    quote: (
      <>
        Fillop gives you the ability to be persistent. I learned exactly what I
        needed to know in the real world. It helped me sell myself to{" "}
        <b>get a new role.</b>
      </>
    ),
    name: "Dr. Bush",
    initials: "DB",
    image: "",
    gradient: "from-[#6b7b8c] to-[#2b3440]",
  },
  {
    quote: (
      <>
        I loved the course about AI Studio. I was not aware of this Google
        tool, but immediately after taking the course, I knew how to use it.
        Within 24 hours, I had a functioning, highly useful app for my law firm.
      </>
    ),
    name: "Daniel E.",
    initials: "DE",
    image: "",
    gradient: "from-[#1a73e8] to-[#0b3d91]",
  },
];

function QuoteMark() {
  return (
    <svg
      viewBox="0 0 30 24"
      aria-hidden="true"
      className="mb-3 h-6 w-[20px] fill-zinc-800 dark:fill-zinc-100"
    >
      <path d="M0 24V13.5C0 5.6 4.3 1 11.6 0l1 3.6C8.4 4.7 6.6 7 6.4 10h5.7v14H0zm17.4 0V13.5C17.4 5.6 21.7 1 29 0l1 3.6c-4.2 1.1-6 3.4-6.2 6.4h5.7v14H17.4z" />
    </svg>
  );
}

function Chevron({ className = "h-3 w-3" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={`${className} fill-none stroke-current`}
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

function Avatar({ image, initials, gradient, name }) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-[15px] w-[15px] flex-none rounded-full object-cover"
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      className={`grid h-[35px] w-[35px] border-6 flex-none place-items-center rounded-full bg-gradient-to-br ${gradient} text-[12px] font-bold text-white`}
    >
      {initials}
    </div>
  );
}

export default function Testimonials() {
  const trackRef = useRef(null);
  const [atEnd, setAtEnd] = useState(false);

  const update = () => {
    const el = trackRef.current;
    if (!el) return;
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const scrollNext = () => {
    const el = trackRef.current;
    const card = el?.querySelector("article");
    if (!el || !card) return;
    el.scrollBy({ left: card.offsetWidth + 24, behavior: "smooth" });
  };

  return (
    <section className="mx-auto max-w-[1280px] bg-white px-6 pb-10 pt-20 font-sans text-zinc-800 dark:bg-zinc-900 dark:text-zinc-100">
      <h2 className="mb-10 text-[clamp(1.5rem,2.6vw,2rem)] leading-tight tracking-tight">
       Join others transforming their lives through learning
      </h2>

      <div className="relative">
        <div
          ref={trackRef}
          onScroll={update}
          className="flex snap-x snap-mandatory gap-6 overflow-x-auto p-0.5 motion-reduce:scroll-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {testimonials.map((t) => (
            <article
              key={t.name}
              className="flex min-w-[280px] flex-none basis-[80%] snap-start flex-col rounded-xl border border-zinc-300 bg-white p-6 dark:border-zinc-700 dark:bg-zinc-900 md:min-h-[300px] md:basis-[calc((100%_-_72px)/3.6)]"
            >
              <QuoteMark />
              <p className="max-w-[60ch] text-[13px] leading-relaxed [&_b]:font-bold">
                {t.quote}
              </p>

              <div className="mt-auto flex items-center gap-2 pt-2">
                <Avatar {...t} />
                <div className="text-[12px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                  {t.name}
                </div>
              </div>

            </article>
          ))}
        </div>

        {!atEnd && (
          <button
            type="button"
            onClick={scrollNext}
            aria-label="Next testimonials"
            className="absolute right-[-6px] top-1/2 hidden h-14 w-14 -translate-y-1/2 place-items-center rounded-full border border-zinc-300 bg-white text-zinc-800 shadow-[0_2px_12px_rgba(0,0,0,0.18)] hover:bg-zinc-50 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-violet-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800 md:grid"
          >
            <Chevron className="h-[22px] w-[22px]" />
          </button>
        )}
      </div>

    </section>
  );
}